"use client";

import { Loader2, Send } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { ChatMessage as ChatMessageComponent } from "./chat-message";
import type { ChatMessage } from "@/hooks/use-recruiter-chat";
import type {
  ConnectionState,
  SearchProgressEntry,
} from "@/hooks/use-recruiter-chat";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ChatPanelProps {
  messages: ChatMessage[];
  onSendMessage: (content: string) => Promise<void>;
  connectionState: ConnectionState;
  searchProgress: SearchProgressEntry[];
  isLoading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChatPanel({
  messages,
  onSendMessage,
  connectionState,
  searchProgress,
  isLoading,
  error,
}: ChatPanelProps) {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isStreaming =
    connectionState === "streaming" || connectionState === "connecting";

  // Auto-scroll on new messages or content changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, searchProgress]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // Max height ~ 4 lines (approx 6rem)
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // Handle send
  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming) return;
    setInputValue("");
    // Reset textarea height after clearing
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    await onSendMessage(trimmed);
  }, [inputValue, isStreaming, onSendMessage]);

  // Handle keyboard
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <div style={styles.container}>
      {/* Inline keyframes for typing indicator */}
      <style>{typingKeyframes}</style>

      {/* Messages area */}
      <div style={styles.messagesArea}>
        {messages.length === 0 && !isLoading && (
          <div style={styles.emptyState}>
            <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>
              AI Recruiter
            </p>
            <p
              style={{
                margin: "0.5rem 0 0",
                fontSize: "0.88rem",
                color: "var(--text-secondary)",
              }}
            >
              Describe the role you're hiring for and I'll help you find the
              best candidates.
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessageComponent key={msg.id} message={msg} />
        ))}

        {/* Search progress cards */}
        {searchProgress.length > 0 && (
          <div style={styles.searchProgressContainer}>
            {searchProgress.map((sp) => (
              <div key={sp.query} style={styles.searchCard}>
                {sp.status === "searching" ? (
                  <Loader2
                    size={14}
                    style={{
                      color: "var(--text-secondary)",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : (
                  <span style={{ color: "var(--text-secondary)", fontSize: 14 }}>
                    &#x2713;
                  </span>
                )}
                <span style={styles.searchText}>
                  {sp.status === "searching"
                    ? `Searching: ${sp.query}`
                    : `Found ${sp.count ?? 0} candidates for "${sp.query}"`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Typing indicator */}
        {isStreaming && (
          <div style={styles.typingIndicator}>
            <span className="typing-dot" style={styles.dot} />
            <span
              className="typing-dot"
              style={{ ...styles.dot, animationDelay: "0.15s" }}
            />
            <span
              className="typing-dot"
              style={{ ...styles.dot, animationDelay: "0.3s" }}
            />
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={styles.errorMessage}>
            <p style={{ margin: 0, fontSize: "0.85rem" }}>{error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div style={styles.inputArea}>
        <div style={styles.inputRow}>
          <textarea
            className="chat-textarea"
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe the role you're looking for..."
            disabled={isStreaming || isLoading}
            rows={1}
            style={{
              ...styles.textarea,
              opacity: isStreaming || isLoading ? 0.5 : 1,
            }}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isStreaming || isLoading}
            style={{
              ...styles.sendButton,
              opacity:
                !inputValue.trim() || isStreaming || isLoading ? 0.35 : 1,
              cursor:
                !inputValue.trim() || isStreaming || isLoading
                  ? "not-allowed"
                  : "pointer",
            }}
            aria-label="Send message"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Typing indicator keyframes
// ---------------------------------------------------------------------------

const typingKeyframes = `
@keyframes typing-pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.chat-textarea { scrollbar-width: none; -ms-overflow-style: none; }
.chat-textarea::-webkit-scrollbar { display: none; }
`;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: "var(--background)",
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "1.5rem 1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    textAlign: "center" as const,
    color: "var(--text-primary)",
    padding: "2rem",
  },
  searchProgressContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
    paddingLeft: "0.25rem",
  },
  searchCard: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.4rem 0.75rem",
    borderRadius: "8px",
    background: "color-mix(in srgb, var(--surface-elevated) 70%, transparent)",
    fontSize: "0.82rem",
  },
  searchText: {
    color: "var(--text-secondary)",
  },
  typingIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "0.5rem 0.75rem",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "var(--text-secondary)",
    animation: "typing-pulse 1.2s ease-in-out infinite",
  } as React.CSSProperties,
  errorMessage: {
    padding: "0.6rem 0.85rem",
    borderRadius: "8px",
    background: "color-mix(in srgb, var(--accent-red) 15%, var(--surface-elevated))",
    color: "var(--accent-red)",
  },
  inputArea: {
    borderTop: "1px solid var(--border)",
    padding: "0.75rem 1rem",
    background: "var(--surface)",
  },
  inputRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "0.5rem",
  },
  textarea: {
    flex: 1,
    resize: "none" as const,
    border: "1px solid var(--border)",
    borderRadius: "12px",
    padding: "0.6rem 0.85rem",
    background: "var(--surface-elevated)",
    color: "var(--text-primary)",
    fontSize: "0.9rem",
    lineHeight: "1.45",
    outline: "none",
    maxHeight: "6rem",
    overflow: "auto",
  },
  sendButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: "10px",
    border: "none",
    background: "var(--accent-blue)",
    color: "#fff",
    flexShrink: 0,
    transition: "opacity 0.15s",
  },
};
